import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../utils/supabase";
import { useAuth } from "./AuthContext";
import toast from 'react-hot-toast';

export interface ReleasesContextType {
  // Still expose as array for backward compatibility
  checkedReleases: number[];
  toggleListen: (releaseId: number) => Promise<void>;
  isSubmitting: boolean;
  loadingReleaseId: number | null;
  error: string | null;
  // Helper method for O(1) lookup
  isReleaseChecked: (releaseId: number) => boolean;
}

export const ReleasesContext = createContext<ReleasesContextType>({
  checkedReleases: [],
  toggleListen: async () => {},
  isSubmitting: false,
  loadingReleaseId: null,
  error: null,
  isReleaseChecked: () => false
});

export const ReleasesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userId, isAuthenticated } = useAuth();
  // Use only the Set for internal storage
  const [checkedReleasesSet, setCheckedReleasesSet] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingReleaseId, setLoadingReleaseId] = useState<number | null>(null);
  const [lastToggleTime, setLastToggleTime] = useState<number>(0);

  // Load initial listened releases
  useEffect(() => {
    const loadListenedReleases = async () => {
      if (!userId || !isAuthenticated) return;
      
      try {
        const { data: listenedReleases, error: listenError } = await supabase
          .from('user_listened_releases')
          .select('release_id')
          .eq('user_id', userId);

        if (listenError) throw listenError;

        const initialCheckedReleases = listenedReleases?.map(lr => lr.release_id) || [];
        setCheckedReleasesSet(new Set(initialCheckedReleases));
      } catch (err) {
        console.error('Error loading listened releases:', err);
        setError('Failed to load listened releases');
      }
    };

    loadListenedReleases();
  }, [userId, isAuthenticated]);

  // Helper function to parse Supabase errors
  const getErrorMessage = (error: any): string => {
    // Check for rate limiting errors
    if (error?.message?.includes('rate limit') || 
        error?.message?.includes('too many requests') ||
        error?.code === '429' ||
        error?.status === 429) {
      return 'You\'re updating too quickly. Please wait a moment before trying again.';
    }
    
    // Check for network errors
    if (error?.message?.includes('network') || 
        error?.message?.includes('connection')) {
      return 'Network error. Please check your connection and try again.';
    }
    
    // Default error message
    return 'Failed to update. Please try again.';
  };

  // Efficient O(1) lookup function
  const isReleaseChecked = useCallback((releaseId: number): boolean => {
    return checkedReleasesSet.has(releaseId);
  }, [checkedReleasesSet]);

  // Toggle listen status for a release
  const toggleListen = useCallback(async (releaseId: number) => {
    if (!userId || !isAuthenticated || !releaseId) return;
    
    // Prevent clicking the same release that's currently loading
    if (loadingReleaseId === releaseId) {
      return;
    }
    
    // Check for rate limiting by enforcing a minimum interval between requests
    const now = Date.now();
    const timeSinceLastToggle = now - lastToggleTime;
    
    // Apply rate limiting regardless of loading state
    if (timeSinceLastToggle < 300) {
      toast.error('Please slow down, you\'re toggling too fast!', {
        duration: 3000,
        position: 'bottom-center',
      });
      return;
    }
    
    // Store previous state for error recovery
    const previousCheckedReleasesSet = new Set(checkedReleasesSet);
    
    try {
      // O(1) lookup with Set
      const isAdding = !checkedReleasesSet.has(releaseId);
      
      // Update local state first for immediate UI feedback
      const newCheckedReleasesSet = new Set(checkedReleasesSet);
      if (isAdding) {
        newCheckedReleasesSet.add(releaseId);
      } else {
        newCheckedReleasesSet.delete(releaseId);
      }
      setCheckedReleasesSet(newCheckedReleasesSet);
      
      // Set loading state
      setIsSubmitting(true);
      setLoadingReleaseId(releaseId);
      setLastToggleTime(now);
      setError(null);
      
      // Directly modify the database for just this one release
      const { error: dbError } = isAdding
        ? await supabase
            .from('user_listened_releases')
            .upsert({ user_id: userId, release_id: releaseId }, { onConflict: 'user_id,release_id' })
        : await supabase
            .from('user_listened_releases')
            .delete()
            .match({ user_id: userId, release_id: releaseId });
      
      if (dbError) {
        throw dbError;
      }
      
      // Show success toast
      toast.success(isAdding ? 'Marked as listened' : 'Unmarked as listened', {
        duration: 1500,
        position: 'bottom-center',
        style: {
          background: isAdding ? '#10B981' : '#6B7280',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '8px',
        },
        iconTheme: {
          primary: '#fff',
          secondary: isAdding ? '#10B981' : '#6B7280',
        },
      });
      
    } catch (error) {
      console.error('Error updating listen status:', error);
      
      // Revert to previous state on error
      setCheckedReleasesSet(previousCheckedReleasesSet);
      
      // Get specific error message
      const errorMessage = getErrorMessage(error);
      setError('Failed to update listen status');
      
      toast.error(errorMessage, {
        duration: 3000,
        position: 'bottom-center',
      });
    } finally {
      setIsSubmitting(false);
      setLoadingReleaseId(null);
    }
  }, [userId, isAuthenticated, checkedReleasesSet, loadingReleaseId, lastToggleTime]);

  // Convert Set to Array only when needed for the context value
  // This is a derived value, not a separate state
  const checkedReleases = useMemo(() => {
    return Array.from(checkedReleasesSet);
  }, [checkedReleasesSet]);

  // Memoize the context value
  const contextValue = useMemo(() => ({
    checkedReleases,
    toggleListen,
    isSubmitting,
    loadingReleaseId,
    error,
    isReleaseChecked
  }), [
    checkedReleases,
    toggleListen,
    isSubmitting,
    loadingReleaseId,
    error,
    isReleaseChecked
  ]);

  return (
    <ReleasesContext.Provider value={contextValue}>
      {children}
    </ReleasesContext.Provider>
  );
};

// Custom hook for consuming the releases context
export const useReleases = () => {
  const context = useContext(ReleasesContext);
  if (!context) {
    throw new Error("useReleases must be used within a ReleasesProvider");
  }
  return context;
}; 